import React, { useState, useRef } from 'react';
import { FaFile, FaTimes, FaTrash } from 'react-icons/fa';
import * as toGeoJSON from 'togeojson';
import * as turf from '@turf/turf';
import { Info } from 'lucide-react';
import FileFormatModal from './FileFormatModal';

const KmlRead = ({ setOpenImgSelecter, bond, flag }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileCsv, setSelectedFileCsv] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [Loading, setLoading] = useState(false);


  const [error, setError] = useState(null);
  const [enableCsvUpload, setEnableCsvUpload] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  // Ref for the KML file input
  const kmlFileInputRef = useRef(null);
  // Ref for the CSV file input
  const csvFileInputRef = useRef(null);

  // Handle KML file change (for file input)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".kml")) {
      setSelectedFile(file);
      handleFileRead(file); // Read and process the file
    } else {
      setError("Invalid file type. Please upload a .kml file.");
    }
  };

  // Handle KML file drop (for drag and drop)
  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(".kml")) {
      setSelectedFile(file);
      handleFileRead(file); // Read and process the file
    } else {
      setError("Invalid file type. Please upload a .kml file.");
    }
  };

  // Allow drag over the file drop area
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Remove the selected KML file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null); // Clear any existing errors
    // Reset the KML file input
    if (kmlFileInputRef.current) {
      kmlFileInputRef.current.value = '';
    }
  };

  // Parse KML and convert it to GeoJSON
  const handleFileRead = (file) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      try {
        const kmlText = event.target.result;

        const parser = new DOMParser();
        const kml = parser.parseFromString(kmlText, "application/xml");

        // Check for parsing errors
        const parserError = kml.getElementsByTagName("parsererror");
        if (parserError.length > 0) {
          console.error("Parser Error:", parserError[0].textContent); // Log the specific parser error
          setError("Failed to parse KML file. Invalid file structure.");
          return;
        }

        // Convert the KML to GeoJSON using togeojson
        const geoJson = toGeoJSON.kml(kml);
        const coordinates = geoJson.features[0]?.geometry?.coordinates;

        if (coordinates) {

          // Flatten the nested array of coordinates if needed
          const allCoordinates = coordinates.flat();
          // Use .map() to create a new array with swapped coordinates
          const swappedCoordinates = allCoordinates.map((coord) => {
            return [coord[1], coord[0]]; // Swap longitude and latitude
          });


          if (swappedCoordinates.length > 0) {
            bond(swappedCoordinates)
            const polygon = turf.polygon([allCoordinates]);
            const areaInSquareMeters = turf.area(polygon);
            const areaInAcres = areaInSquareMeters / 4046.86;
            console.log("add kml areaInAcres from upload img ", areaInAcres);
          }
        } else {
          setError("Could not find coordinates in the KML file.");
        }

        // Clear any previous error
        setError(null);
      } catch (err) {
        // Log the specific error
        console.error("File Read Error:", err);
        setError("Error reading the file. Please try again.");
      }
    };

    reader.readAsText(file); // Start reading the file as text
  };

  // Handle CSV file change (for file input)
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFileCsv(file);
      setError(null);
    }
  };

  // Handle CSV file drop (for drag and drop)
  const handleCsvFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFileCsv(file);
      setError(null);
    }
  };

  // Remove the selected CSV file
  const handleRemoveCsvFile = () => {
    setSelectedFileCsv(null);
    setError(null); // Clear any existing errors
    // Reset the CSV file input
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (selectedFileCsv) {
      setLoading(true); // Set loading to true immediately

      setTimeout(() => {
        flag(true);
        setOpenImgSelecter(false);
        setSelectedFile(null);
        setSelectedFileCsv(null);

        setLoading(false); // Set loading to false after completing actions
      }, 5000);
    }
    else {
      setError("No CSV Uploaded")
    }

    // Reset the file inputs
    if (kmlFileInputRef.current) {
      kmlFileInputRef.current.value = '';
    }
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
    }
  };


  return (
    <div className="absolute w-full h-screen bg-black bg-opacity-50 top-0 left-0 right-0 z-[10000] flex items-center justify-center">
      <div className="relative w-[90%] md:w-[80%] lg:w-[70%] min-h-[400px] bg-white flex flex-col items-center justify-start p-4 rounded-xl">
        <span
          className="absolute right-2 top-2 text-xl cursor-pointer hover:scale-105 duration-300 bg-gray-300 rounded-full p-2"
          onClick={() => setOpenImgSelecter(false)}
        >
          <FaTimes />
        </span>

        <div className="mt-5 w-[70%] mx-auto text-center">
          <p className="text-2xl font-semibold">Add files with field boundaries</p>
          <p className="mt-1 text-sm font-medium text-gray-500">
            We support .kml files for field boundaries.
          </p>
        </div>

        <div className="bg-gray-200 p-3 w-[70%] mt-4 rounded-xl">
          <div
            className="min-h-[200px] border-dashed border-2 border-gray-400 rounded-md p-4 flex items-center justify-center flex-col"
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
          >
            <div className="flex flex-col items-center justify-center">
              <p className="font-semibold text-gray-600">Drag and drop files here</p>
              <p className="text-sm font-medium text-gray-500">1.0 GB max</p>
              <FaFile className="text-2xl text-gray-400 mt-4" />
              <input
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="fileInput"
                accept=".kml"
                ref={kmlFileInputRef} // Add ref to the KML file input
              />
              <label
                htmlFor="fileInput"
                className="bg-green-500 rounded-md px-4 py-2 font-medium cursor-pointer hover:bg-green-600 duration-300 text-white mt-4"
              >
                Select KML file
              </label>
            </div>
          </div>
          {selectedFile && (
            <div className="flex mt-4 w-full justify-between items-center">
              <div className="flex justify-between items-center">
                <FaFile className="text-2xl text-gray-400 mr-2" />
                <div>
                  <p className="text-sm">{selectedFile.name}</p>
                  <p className="text-[12px] text-gray-400">{selectedFile.size} B</p>
                </div>
              </div>
              <span
                onClick={handleRemoveFile}
                className="p-4 bg-gray-300 rounded-md cursor-pointer text-black hover:text-red-500 duration-300"
              >
                <FaTrash />
              </span>
            </div>
          )}
        </div>

        {selectedFile && (
          <div className="w-[70%] mt-4">
            <p className="text-2xl font-semibold">Add files with ground truth points</p>
            <p className="text-sm mt-1 font-medium text-gray-500">
              We support device ID or CSV file both. If you want to upload a CSV file, first enable the feature.
            </p>
            <div className="bg-gray-200 p-3 mt-4 rounded-xl">
              <div className="flex flex-col space-y-4">
                {/* Device ID Input Field */}
                <div>
                  <label htmlFor="crop" className="block mt-1 text-sm font-bold">
                    Select your Crop
                  </label>
                  <select
                    id="crop"
                    className="mt-3 block w-full p-2 border rounded-md border-black"
                  >
                    <option value="">Select Your Field</option>
                    <option value="">Wheat</option>
                    <option value="">Other area</option>


                  </select>
                </div>

                {/* Checkbox to Enable CSV Upload */}
                <div className="flex items-center justify-between">
                  <div className='flex items-center'>
                    <input
                      type="checkbox"
                      id="enableCsvUpload"
                      checked={enableCsvUpload}
                      onChange={(e) => setEnableCsvUpload(e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableCsvUpload" className="ml-2 block text-sm text-gray-900">
                      Enable CSV Upload
                    </label>
                  </div>
                  <div >
                    <Info onClick={openModal} />
                  </div>
                </div>


                {/* File Upload Section (Conditional Rendering) */}
                {enableCsvUpload && (
                  <div
                    className="min-h-[50px] border-dashed border-2 border-gray-400 rounded-md p-4 flex items-center justify-center flex-col"
                    onDrop={handleCsvFileDrop}
                    onDragOver={handleDragOver}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <p className="font-semibold text-gray-600">Drag and drop CSV files here</p>
                      <input
                        type="file"
                        onChange={handleCsvFileChange}
                        style={{ display: "none" }}
                        id="csvFileInput"
                        accept=".csv"
                        ref={csvFileInputRef} // Add ref to the CSV file input
                      />
                      <label
                        htmlFor="csvFileInput"
                        className="bg-green-500 rounded-md px-4 py-2 font-medium cursor-pointer hover:bg-green-600 duration-300 text-white mt-4"
                      >
                        Select CSV file
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Display Selected CSV File */}
              {selectedFileCsv && (
                <div className="flex mt-4 w-full justify-between items-center">
                  <div className="flex justify-between items-center">
                    <FaFile className="text-2xl text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm">{selectedFileCsv.name}</p>
                      <p className="text-[12px] text-gray-400">{selectedFileCsv.size} B</p>
                    </div>
                  </div>
                  <span
                    onClick={handleRemoveCsvFile}
                    className="p-4 bg-gray-300 rounded-md cursor-pointer text-black hover:text-red-500 duration-300"
                  >
                    <FaTrash />
                  </span>
                </div>
              )}
            </div>
            {error && (
              <p className="text-red-500 text-sm font-medium mt-2">
                {error}
              </p>
            )}
          </div>

        )}

        {selectedFile && (
          <div className="flex items-center justify-between w-[50%] mt-4 bg-[#333] p-2 rounded-md gap-2">
            <button
              onClick={() => setOpenImgSelecter(false)}
              className="w-1/2 py-3 rounded bg-[#474747] text-white border-none outline-none cursor-pointer hover:bg-black duration-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="w-1/2 py-3 rounded bg-green-500 text-white border-none outline-none cursor-pointer hover:bg-green-600 duration-300 hover:text-white"
            >
              {Loading ? 'Loading...' : 'Done'}
            </button>
          </div>
        )}
        {isModalOpen && <FileFormatModal onClose={closeModal} />}
      </div>
    </div>
  );
};

export default KmlRead;